/**
 * Native audio session — React Native's replacement for the web adapter's
 * hidden `<audio>` elements.
 *
 * On web, remote audio needs a DOM element to play into. On mobile it needs
 * the opposite: nothing to attach, but an OS-level audio session that has to
 * be configured and activated before the call and released after it, or the
 * mic indicator stays lit and the app keeps ducking other audio.
 *
 * The configuration encodes the mobile audio invariants (AGENTS.md rule 10):
 * communication mode so the call survives backgrounding and routes through the
 * earpiece/bluetooth stack rather than the media stream, and a preferred
 * output list that follows a headset or bluetooth device when one appears.
 *
 * Every call is best-effort. A device that refuses to hand over the audio
 * session must not take the call down with it — the user still gets a
 * (possibly routed-oddly) conversation, which beats a hard failure.
 */

import { AndroidAudioTypePresets, AudioSession } from "@livekit/react-native";

export interface AudioSessionPort {
  /** Configure + activate. Safe to call repeatedly; only the first wins. */
  start(): Promise<void>;
  /** Release the session. Safe to call when never started. */
  stop(): Promise<void>;
}

/**
 * Routing preference, most to least specific. Bluetooth and wired headsets
 * win when present; speaker is the fallback for a voice widget the user is
 * holding at arm's length, and the earpiece is last.
 */
const PREFERRED_OUTPUTS = ["bluetooth", "headset", "speaker", "earpiece"] as const;

export const createAudioSession = (): AudioSessionPort => {
  let active = false;

  const start = async (): Promise<void> => {
    if (active) return;
    // Optimistic: mark active before awaiting so two overlapping connects
    // cannot both configure the session (the second would fight the first).
    active = true;
    try {
      await AudioSession.configureAudio({
        android: {
          audioTypeOptions: AndroidAudioTypePresets.communication,
          preferredOutputList: [...PREFERRED_OUTPUTS],
        },
        ios: { defaultOutput: "speaker" },
      });
      await AudioSession.startAudioSession();
    } catch {
      // Session refused — the call still connects; routing falls back to
      // whatever the OS had configured.
    }
  };

  const stop = async (): Promise<void> => {
    if (!active) return;
    active = false;
    try {
      await AudioSession.stopAudioSession();
    } catch {
      /* nothing left to release */
    }
  };

  return { start, stop };
};
